import { useState } from 'react';
import { IconButton } from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { CanvasContentId } from 'types/canvas';
import { SGTooltip } from 'components/core/SGTooltip';
import { useCanvasGroupCtx } from './CanvasGroupContext';
import { GroupPickerMenu } from './GroupPickerMenu';

export interface CanvasGroupLinkButtonProps {
  componentId: number;
  contentId: CanvasContentId;
}

/**
 * Per-component "link to symbol group" affordance — drop into any canvas
 * host's header next to `<ExpandableContentButton />`.
 *
 * Renders nothing for components whose `ComponentMeta.singleSymbol === false`
 * (scanners, calendars, etc.), so hosts can mount it unconditionally without
 * worrying about eligibility.
 *
 * When the component is in a group, the icon takes the group's color; when
 * not, it falls back to the theme's primary color (matching the rest of the
 * canvas header buttons).
 */
export const CanvasGroupLinkButton = ({
  componentId,
  contentId,
}: CanvasGroupLinkButtonProps) => {
  const { groupForComponent, isComponentEligible } = useCanvasGroupCtx();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!isComponentEligible(contentId)) {
    return null;
  }

  const group = groupForComponent(componentId);
  const tooltipText =
    group != null
      ? `Group: ${group.name} — symbol changes sync across this group`
      : 'Link this component to a symbol group';

  return (
    <>
      <SGTooltip title={tooltipText}>
        <IconButton
          data-ph-capture-attribute-element="canvas-group-link"
          className="canvas-no-drag"
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          // Group color overrides the default primary color so the chip is
          // immediately recognizable.
          sx={group != null ? { color: group.color } : undefined}
          aria-label="Symbol group"
        >
          <WorkspacesIcon fontSize="small" />
        </IconButton>
      </SGTooltip>

      <GroupPickerMenu
        componentId={componentId}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};
